/**
 * Language and City Selection Popup
 * Handles the popup functionality for first-time visitors to select language and delivery city
 */

class LanguageCityPopup {
  constructor() {
    this.popup = null;
    this.citySelectionSection = null;
    this.selectedLanguage = null;
    this.selectedCity = null;
    this.isInitialized = false;
    this.storageKey = 'fnp_language_city_selection';
    
    this.init();
  }
  
  init() {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setup());
    } else {
      this.setup();
    }
  }
  
  setup() {
    if (this.isInitialized) return;
    
    this.popup = document.getElementById('language-city-popup');
    this.citySelectionSection = document.getElementById('city-selection-section');
    
    if (!this.popup || !this.citySelectionSection) {
      console.warn('Language city popup elements not found');
      return;
    }
    
    // Check if popup should be shown
    if (this.shouldShowPopup()) {
      this.showPopup();
      this.bindEvents();
      
      // Set initial language based on current page language
      const currentLang = document.documentElement.lang || 'en';
      this.selectedLanguage = currentLang.startsWith('ar') ? 'ar' : 'en';
      
      this.isInitialized = true;
    } else {
      // If popup shouldn't show, check if we should preserve current city selection
      this.checkAndPreserveCurrentCity();
      // Then try to apply saved preferences (but city will be preserved if needed)
      this.applySavedPreferences();
      
      // Sync city selection across all components
      this.syncCitySelection();
    }
    
    // Set up language change detection
    this.setupLanguageChangeDetection();
  }
  
  /**
   * Set up detection for manual language changes through the header
   */
  setupLanguageChangeDetection() {
    // Listen for language changes on the document
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'lang') {
          const currentLang = document.documentElement.lang || 'en';
          const newLanguage = currentLang.startsWith('ar') ? 'ar' : 'en';
          
          // Update the saved language if it's different
          if (this.selectedLanguage && this.selectedLanguage !== newLanguage) {
            this.selectedLanguage = newLanguage;
            this.updateSavedLanguage(newLanguage);
            
            // Update the displayed city names and title
            this.updateCityNames();
          }
        }
      });
    });
    
    // Start observing the document element for lang attribute changes
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['lang']
    });
    
  }
  
  /**
   * Check if popup should be shown
   * @returns {boolean}
   */
  shouldShowPopup() {
    // Check if user has already made a selection
    const savedSelection = this.getSavedSelection();
    if (savedSelection && savedSelection.language && savedSelection.city) {
      return false; // User has made a selection, don't show popup
    }
    
    // Check if user has dismissed the popup recently (within last 24 hours)
    try {
      const dismissedFlag = localStorage.getItem(this.storageKey + '_dismissed');
      if (dismissedFlag) {
        const dismissed = JSON.parse(dismissedFlag);
        const hoursSinceDismissed = (Date.now() - dismissed.timestamp) / (1000 * 60 * 60);
        
        // If dismissed less than 24 hours ago, don't show popup
        if (hoursSinceDismissed < 24) {
          return false;
        }
      }
    } catch (error) {
      console.warn('Error checking dismissed flag:', error);
    }
    
    return true; // Show popup if no selection and not recently dismissed
  }
  
  /**
   * Get saved selection from local storage
   * @returns {Object|null}
   */
  getSavedSelection() {
    try {
      const saved = localStorage.getItem(this.storageKey);
      return saved ? JSON.parse(saved) : null;
    } catch (error) {
      console.warn('Error reading from localStorage:', error);
      return null;
    }
  }
  
  /**
   * Save selection to local storage
   * @param {string} language - Selected language
   * @param {string} city - Selected city
   */
  saveSelection(language, city) {
    try {
      const selection = {
        language: language,
        city: city,
        timestamp: Date.now()
      };
      localStorage.setItem(this.storageKey, JSON.stringify(selection));
    } catch (error) {
      console.warn('Error saving to localStorage:', error);
    }
  }
  
  /**
   * Apply saved preferences to the site
   */
  applySavedPreferences() {
    const savedSelection = this.getSavedSelection();
    if (savedSelection && savedSelection.language && savedSelection.city) {
      
      // Set the selected values
      this.selectedLanguage = savedSelection.language;
      this.selectedCity = savedSelection.city;
      
      // Check if there's already a current city selection that should be preserved
      const currentCity = localStorage.getItem('city_value');
      const currentCityAlt = localStorage.getItem('city');
      
      if (currentCity || currentCityAlt) {
        this.selectedCity = currentCity || currentCityAlt;
      }
      
      // Dispatch event for other components to use
      this.dispatchCustomEvent('languageCitySelected', {
        language: this.selectedLanguage,
        city: this.selectedCity
      });
      
      // Check if the current page language matches the saved language
      // If not, it means the user manually changed the language through the header
      const currentLang = document.documentElement.lang || 'en';
      const currentPageLanguage = currentLang.startsWith('ar') ? 'ar' : 'en';
      if (currentPageLanguage !== this.selectedLanguage) {
        
        // Update the saved language to match the current page
        this.selectedLanguage = currentPageLanguage;
        this.updateSavedLanguage(currentPageLanguage);
        
        // Update the displayed city names and title
        this.updateCityNames();
        
        // Only apply city change if needed
        if (this.selectedCity && this.selectedCity !== (currentCity || currentCityAlt)) {
          setTimeout(() => {
            this.applyCityChange();
          }, 500);
        }
        return;
      }
      
      // If languages match, only apply city change to preserve user's city selection
      
      // Only apply city change if needed
      if (this.selectedCity && this.selectedCity !== (currentCity || currentCityAlt)) {
        setTimeout(() => {
          this.applyCityChange();
        }, 500);
      }
    }
  }
  
  /**
   * Update the saved language in localStorage without changing other preferences
   */
  updateSavedLanguage(newLanguage) {
    try {
      const savedSelection = this.getSavedSelection();
      if (savedSelection) {
        savedSelection.language = newLanguage;
        savedSelection.timestamp = Date.now();
        localStorage.setItem(this.storageKey, JSON.stringify(savedSelection));
      }
    } catch (error) {
      console.warn('Error updating saved language:', error);
    }
  }
  
  /**
   * Clear saved language selection to prevent popup from overriding manual changes
   */
  clearSavedLanguage() {
    try {
      const savedSelection = this.getSavedSelection();
      if (savedSelection && savedSelection.city) {
        // Keep city but remove language
        delete savedSelection.language;
        savedSelection.timestamp = Date.now();
        localStorage.setItem(this.storageKey, JSON.stringify(savedSelection));
      }
    } catch (error) {
      console.warn('Error clearing saved language:', error);
    }
  }
  
  /**
   * Clear saved selection (useful for testing or admin purposes)
   */
  clearSavedSelection() {
    try {
      localStorage.removeItem(this.storageKey);
    } catch (error) {
      console.warn('Error clearing localStorage:', error);
    }
  }
  
  /**
   * Clear dismissed flag (useful for testing or admin purposes)
   */
  clearDismissedFlag() {
    try {
      localStorage.removeItem(this.storageKey + '_dismissed');
    } catch (error) {
      console.warn('Error clearing dismissed flag:', error);
    }
  }
  
  showPopup() {
    if (!this.popup) return;
    
    this.popup.style.display = 'flex';
    document.body.style.overflow = 'hidden';
    
    // Add entrance animation
    this.popup.classList.add('popup-visible');
    
    // Focus management for accessibility
    this.setFocusTrap();
    
    // Announce to screen readers
    this.announceToScreenReader('Language and city selection popup opened');
  }
  
  hidePopup() {
    if (!this.popup) return;
    
    this.popup.style.display = 'none';
    document.body.style.overflow = '';
    this.popup.classList.remove('popup-visible');
    
    // Remove focus trap
    this.removeFocusTrap();
    
    // CRITICAL FIX: Remove any lingering pointer-events issues
    // Ensure the popup overlay doesn't intercept clicks after closing
    const overlay = document.querySelector('.popup-overlay');
    if (overlay) {
      overlay.style.pointerEvents = 'none';
      overlay.style.display = 'none';
      overlay.style.zIndex = '-1';
      // Remove the overlay from DOM completely to prevent any interference
      setTimeout(() => {
        if (overlay.parentNode) {
          overlay.parentNode.removeChild(overlay);
        }
      }, 100);
    }
    
    // Additional cleanup: Remove any event listeners that might interfere
    this.popup.removeEventListener('click', this.popupClickHandler);
    
    // Trigger custom event for other scripts
    if (this.selectedLanguage && this.selectedCity) {
      this.dispatchCustomEvent('languageCitySelected', {
        language: this.selectedLanguage,
        city: this.selectedCity
      });
      
      // Announce to screen readers
      this.announceToScreenReader(`Selected ${this.selectedLanguage} language and ${this.selectedCity} city`);
      
      // Apply the selection to the website
      this.applySelectionToWebsite();
    }
  }
  
  /**
   * Dismiss the popup
   */
  dismissPopup() {
    this.hidePopup();
    
    // If user dismisses without making a selection, save a "dismissed" flag
    // This prevents the popup from showing again immediately
    if (!this.selectedLanguage || !this.selectedCity) {
      try {
        const dismissedFlag = {
          dismissed: true,
          timestamp: Date.now()
        };
        localStorage.setItem(this.storageKey + '_dismissed', JSON.stringify(dismissedFlag));
      } catch (error) {
        console.warn('Error saving dismissed flag:', error);
      }
    }
    
    // Dispatch custom event for other components to listen to
    this.dispatchCustomEvent('popupDismissed');
  }
  
  bindEvents() {
    // Language selection
    const languageBtns = document.querySelectorAll('.language-btn');
    languageBtns.forEach(btn => {
      btn.addEventListener('click', async (e) => {
        const selectedLang = e.currentTarget.dataset.language;
        

        
        this.selectedLanguage = selectedLang;
        
        this.showCitySelection();
      });
      
      // Keyboard support
      btn.addEventListener('keydown', async (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          const selectedLang = e.currentTarget.dataset.language;
          

          
          this.selectedLanguage = selectedLang;
          
          this.showCitySelection();
        }
      });
    });
    
    // City selection
    const cityOptions = document.querySelectorAll('.city-option');
    cityOptions.forEach(option => {
      option.addEventListener('click', async (e) => {
        this.selectedCity = e.currentTarget.dataset.city;
        
        // IMMEDIATE UI UPDATE - Hide popup first for instant feedback
        this.hidePopup();
        
        // Save selection to local storage immediately
        if (this.selectedLanguage && this.selectedCity) {
          this.saveSelection(this.selectedLanguage, this.selectedCity);
        }
        
        // DEFER HEAVY OPERATIONS - Run cart clearing asynchronously
        setTimeout(async () => {
          try {
            const cartItems = document.querySelector('cart-items') || document.querySelector('cart-drawer-items');
            if (cartItems && typeof cartItems.clearCart === 'function') {
              await cartItems.clearCart();
            }
          } catch (error) {
            console.warn('Failed to clear cart before city selection:', error);
          }
        }, 0);
      });
      
      // Keyboard support
      option.addEventListener('keydown', async (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this.selectedCity = e.currentTarget.dataset.city;
          
          // IMMEDIATE UI UPDATE - Hide popup first for instant feedback
          this.hidePopup();
          
          // Save selection to local storage immediately
          if (this.selectedLanguage && this.selectedCity) {
            this.saveSelection(this.selectedLanguage, this.selectedCity);
          }
          
          // DEFER HEAVY OPERATIONS - Run cart clearing asynchronously
          setTimeout(async () => {
            try {
              const cartItems = document.querySelector('cart-items') || document.querySelector('cart-drawer-items');
              if (cartItems && typeof cartItems.clearCart === 'function') {
                await cartItems.clearCart();
              }
            } catch (error) {
              console.warn('Failed to clear cart before city selection:', error);
            }
          }, 0);
        }
      });
    });
    
    // Close on overlay click
    const overlay = document.querySelector('.popup-overlay');
    if (overlay) {
      overlay.addEventListener('click', () => {
        this.dismissPopup();
      });
    }
    
    // Close on escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.popup.style.display === 'flex') {
        this.dismissPopup();
      }
    });
    
    // Close on outside click (optional)
    this.popupClickHandler = (e) => {
      if (e.target === this.popup) {
        this.dismissPopup();
      }
    };
    this.popup.addEventListener('click', this.popupClickHandler);
  }
  
  showCitySelection() {
    if (!this.citySelectionSection) return;
    
    
    // Only proceed if a language is actually selected
    if (!this.selectedLanguage) {
      return;
    }
    
    // Update language button styling
    const languageBtns = document.querySelectorAll('.language-btn');
    languageBtns.forEach(btn => {
      btn.classList.remove('selected');
      if (btn.dataset.language === this.selectedLanguage) {
        btn.classList.add('selected');
      }
    });
    
    // Show city selection section
    this.citySelectionSection.style.display = 'block';
    
    // Small delay to ensure DOM is updated before updating city names
    setTimeout(() => {
      // Update city names based on selected language
      this.updateCityNames();
    }, 100);
    
    // Focus first city option
    const firstCityOption = document.querySelector('.city-option');
    if (firstCityOption) {
      firstCityOption.focus();
    }
    
    // Announce to screen readers
    this.announceToScreenReader('City selection available');
  }
  
  /**
   * Update city names based on selected language
   */
  updateCityNames() {
    
    // Update city selection title
    const citySelectionTitle = this.citySelectionSection.querySelector('.city-selection-title');
    if (citySelectionTitle && citySelectionTitle.dataset.fullTitle) {
      const titleParts = citySelectionTitle.dataset.fullTitle.split('##');
      
      if (this.selectedLanguage === 'en') {
        // For English, show English title (second part)
        citySelectionTitle.textContent = titleParts[1].trim(); // "Delivery City"
      } else if (this.selectedLanguage === 'ar') {
        // For Arabic, show Arabic title (first part)
        citySelectionTitle.textContent = titleParts[0].trim(); // "مدينة التسليم"
      }
    }
    
    // City translation mapping for fallback when cities don't have Arabic names in data attributes
    const cityTranslations = {
      'Riyadh': 'الرياض',
      'Jeddah': 'جدة',
      'Al Khobar': 'الخبر',
      'Dammam': 'الدمام',
      'Dhahran': 'الظهران',
      'Jubail': 'الجبيل',
      'Qatif': 'القطيف',
      'Madinah': 'المدينة',
      'Meccah': 'مكه',

    };
    
    const cityNames = this.citySelectionSection.querySelectorAll('.city-name');
    
    cityNames.forEach((cityNameSpan, index) => {
      const fullTitle = cityNameSpan.dataset.fullTitle;
      
      if (fullTitle) {
        const parts = fullTitle.split('##');
        
        // Clear any existing text first
        cityNameSpan.textContent = '';
        
        if (this.selectedLanguage === 'en') {
          if (parts.length > 1) {
            // For English, take the second part (English name)
            const englishName = parts[1].trim();
            cityNameSpan.textContent = englishName;
          } else {
            cityNameSpan.textContent = fullTitle;
          }
        } else if (this.selectedLanguage === 'ar') {
          if (parts.length > 1) {
            // For Arabic, take the first part (Arabic name) if both languages are available
            const arabicName = parts[0].trim();
            cityNameSpan.textContent = arabicName;
          } else {
            // Fallback: use translation mapping for cities without Arabic names in data
            const englishName = fullTitle.trim();
            const arabicTranslation = cityTranslations[englishName];
            if (arabicTranslation) {
              cityNameSpan.textContent = arabicTranslation;
            } else {
              cityNameSpan.textContent = fullTitle;
            }
          }
        } else {
          cityNameSpan.textContent = fullTitle;
        }
        
      } else {
      }
    });
    
  }
  
  setFocusTrap() {
    // Create focus trap for accessibility
    this.focusableElements = this.popup.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    
    this.firstFocusableElement = this.focusableElements[0];
    this.lastFocusableElement = this.focusableElements[this.focusableElements.length - 1];
    
    // Focus first element
    if (this.firstFocusableElement) {
      this.firstFocusableElement.focus();
    }
    
    // Add focus trap event listener
    this.popup.addEventListener('keydown', this.handleFocusTrap.bind(this));
  }
  
  removeFocusTrap() {
    if (this.popup) {
      this.popup.removeEventListener('keydown', this.handleFocusTrap.bind(this));
    }
  }
  
  handleFocusTrap(e) {
    if (e.key === 'Tab') {
      if (e.shiftKey) {
        if (document.activeElement === this.firstFocusableElement) {
          e.preventDefault();
          this.lastFocusableElement.focus();
        }
      } else {
        if (document.activeElement === this.lastFocusableElement) {
          e.preventDefault();
          this.firstFocusableElement.focus();
        }
      }
    }
  }
  
  announceToScreenReader(message) {
    // Create temporary announcement for screen readers
    const announcement = document.createElement('div');
    announcement.setAttribute('aria-live', 'polite');
    announcement.setAttribute('aria-atomic', 'true');
    announcement.className = 'sr-only';
    announcement.textContent = message;
    
    document.body.appendChild(announcement);
    
    // Remove after announcement
    setTimeout(() => {
      if (announcement.parentNode) {
        announcement.parentNode.removeChild(announcement);
      }
    }, 1000);
  }
  
  dispatchCustomEvent(eventName, detail) {
    const event = new CustomEvent(eventName, {
      detail: detail,
      bubbles: true,
      cancelable: true
    });
    window.dispatchEvent(event);
  }
  
  // Public method to reset popup (useful for testing or admin purposes)
  reset() {
    if (this.popup) {
      this.popup.style.display = 'none';
    }
    
    this.selectedLanguage = null;
    this.selectedCity = null;
    this.isInitialized = false;
    
    // Clear saved selection and dismissed flag from local storage
    this.clearSavedSelection();
    this.clearDismissedFlag();
  }
  
  // Public method to show popup programmatically
  show() {
    if (!this.isInitialized) {
      this.setup();
    }
    
    if (this.popup) {
      this.showPopup();
    }
  }
  
  // Public method to force show popup (ignores local storage)
  forceShow() {
    if (!this.isInitialized) {
      this.setup();
    }
    
    if (this.popup) {
      this.showPopup();
      this.bindEvents();
    }
  }
  
  // Public method to get current selection
  getSelection() {
    return {
      language: this.selectedLanguage,
      city: this.selectedCity
    };
  }
  
  // Public method to check if user has made a selection before
  hasSelection() {
    const savedSelection = this.getSavedSelection();
    return !!(savedSelection && savedSelection.language && savedSelection.city);
  }

  // Public method to manually apply current selection to website
  applyCurrentSelection() {
    if (this.selectedLanguage && this.selectedCity) {
      this.applySelectionToWebsite();
    } else {
    }
  }
  
  // Public method to allow user to change their selection
  changeSelection() {
    // Clear saved selection and dismissed flag, then show popup again
    this.clearSavedSelection();
    this.clearDismissedFlag();
    this.selectedLanguage = null;
    this.selectedCity = null;
    this.isInitialized = false;
    
    // Show popup again
    this.setup();
  }
  
  // Public method to clear saved language when user manually changes it
  clearSavedLanguageSelection() {
    this.clearSavedLanguage();
    this.selectedLanguage = null;
  }

  /**
   * Apply selected language and city to the website
   * This function will automatically click on the matching language and city options
   */
  async applySelectionToWebsite() {
    if (!this.selectedLanguage || !this.selectedCity) {
      return;
    }


    // Check if city/language are actually changing
    const currentCity = localStorage.getItem('city_value') || localStorage.getItem('city');
    const currentLang = document.documentElement.lang || 'en';
    const currentLanguage = currentLang.startsWith('ar') ? 'ar' : 'en';
    
    const isCityChanging = window.CityMatcher ? 
      !window.CityMatcher.match(this.selectedCity, currentCity) : 
      this.selectedCity !== currentCity;
    const isLanguageChanging = this.selectedLanguage !== currentLanguage;
    

    // DEFER HEAVY OPERATIONS - Only clear cart if city is actually changing (not for language-only changes)
    if (isCityChanging) {
      // Use setTimeout to defer cart clearing and allow UI to update first
      setTimeout(async () => {
        try {
          const cartItems = document.querySelector('cart-items') || document.querySelector('cart-drawer-items');
          if (cartItems && typeof cartItems.clearCart === 'function') {
            await cartItems.clearCart();
          }
        } catch (error) {
          console.warn('Failed to clear cart before city change:', error);
        }
      }, 0);
    }

    // Check if required elements exist
    if (!this.checkRequiredElements()) {
      setTimeout(() => this.applySelectionToWebsite(), 1000);
      return;
    }

    // Add a small delay to ensure DOM is ready
    setTimeout(() => {
      // Apply language change
      this.applyLanguageChange();
      
      // Apply city change
      this.applyCityChange();
    }, 500);
  }

  /**
   * Sync city selection across all components
   */
  syncCitySelection() {
    try {
      const currentCity = localStorage.getItem('city_value') || localStorage.getItem('city');
      if (currentCity) {
        
        // Sync function should not dispatch city-changed events
        // It's just syncing existing selection, not changing it
        
        // Also update any global city functions if they exist
        if (window.updateAllProductFormCities) {
          window.updateAllProductFormCities(currentCity);
        }
        
        // Update the header display if it exists
        this.updateHeaderCityDisplay(currentCity);
      }
    } catch (error) {
      console.warn('Error syncing city selection:', error);
    }
  }

  /**
   * Update header city display
   */
  updateHeaderCityDisplay(city) {
    try {
      const headerCityDisplay = document.querySelector('.city-display-text');
      if (headerCityDisplay) {
        const currentLang = document.documentElement.lang || 'en';
        const isArabic = currentLang.startsWith('ar');
        const prefix = isArabic ? 'يسلم إلى: ' : 'Deliver to: ';
        
        // Translate city name based on language
        let displayCity = city;
        if (isArabic) {
          // Use the same translation mapping as in updateCityNames
          const cityTranslations = {
            'Riyadh': 'الرياض',
            'Jeddah': 'جدة',
            'Al Khobar': 'الخبر',
            'Dammam': 'الدمام',
            'Dhahran': 'الظهران',
            'Jubail': 'الجبيل',
            'Qatif': 'القطيف',
            'Madinah' : 'المدينة',
            'meccah' : 'مكه'

          };
          displayCity = cityTranslations[city] || city;
        }
        
        headerCityDisplay.textContent = prefix + displayCity;
        headerCityDisplay.classList.add('loaded');
      }
    } catch (error) {
      console.warn('Error updating header city display:', error);
    }
  }

  /**
   * Check if current city selection should be preserved
   */
  checkAndPreserveCurrentCity() {
    try {
      const currentCity = localStorage.getItem('city_value') || localStorage.getItem('city');
      const savedSelection = this.getSavedSelection();
      
      if (currentCity && savedSelection && savedSelection.city && currentCity !== savedSelection.city) {
        
        // Update the saved selection to use the current city
        const updatedSelection = {
          ...savedSelection,
          city: currentCity
        };
        
        // Save the updated selection
        try {
          localStorage.setItem(this.storageKey, JSON.stringify(updatedSelection));
        } catch (error) {
          console.warn('Error updating saved selection:', error);
        }
      }
    } catch (error) {
      console.warn('Error checking current city preservation:', error);
    }
  }

  /**
   * Check if required elements exist in the DOM
   */
  checkRequiredElements() {
    const languageSelector = document.querySelector('.localization-selector');
    const locationDropdown = document.getElementById('location-dropdown');
    
    const hasLanguageSelector = !!languageSelector;
    const hasLocationDropdown = !!locationDropdown;
    
    
    return hasLanguageSelector && hasLocationDropdown;
  }

  /**
   * Apply language change by clicking on the appropriate language option
   */
  applyLanguageChange() {
    try {
      
      // Find the language selector button
      const languageSelector = document.querySelector('.localization-selector');
      if (!languageSelector) {
        return;
      }

      // Check if we need to change language
      const currentLang = document.documentElement.lang || 'en';
    const currentLanguage = currentLang.startsWith('ar') ? 'ar' : 'en';
      
      if (currentLanguage === this.selectedLanguage) {
        return;
      }

      // Try direct language change first (no dropdown opening)
      if (this.tryDirectLanguageChange()) {
        return;
      }

      // For 2-language setup, use the existing changeToSecondCountry function
      if (window.changeToSecondCountry) {
        // Create a mock event
        const mockEvent = { preventDefault: () => {} };
        window.changeToSecondCountry(mockEvent, this.selectedLanguage);
      } else {
        // Alternative approach: find and click the language option
        this.clickLanguageOption();
      }
    } catch (error) {
      console.error('Error applying language change:', error);
    }
  }

  /**
   * Alternative method to click on language option
   */
  clickLanguageOption() {
    try {
      // Find the language selector and click it to open dropdown
      const languageSelector = document.querySelector('.localization-selector');
      if (languageSelector) {
        languageSelector.click();
        
        // Wait for dropdown to open, then find and click the target language
        setTimeout(() => {
          const languageOptions = document.querySelectorAll('.disclosure__link[data-value]');
          languageOptions.forEach(option => {
            if (option.getAttribute('data-value') === this.selectedLanguage) {
              option.click();
            }
          });
        }, 100);
      }
    } catch (error) {
      console.error('Error clicking language option:', error);
    }
  }

  /**
   * Try to change language without opening dropdown
   */
  tryDirectLanguageChange() {
    try {
      // Check if we can find the language form and submit it directly
      const languageForm = document.querySelector('#HeaderLanguageForm');
      if (languageForm) {
        const localeInput = languageForm.querySelector('input[name="locale_code"]');
        if (localeInput) {
          
          // Update the hidden input value
          localeInput.value = this.selectedLanguage;
          
          // Try to submit the form
          languageForm.submit();
          return true;
        }
      }
      
      // If direct form submission doesn't work, fall back to clicking
      return false;
    } catch (error) {
      console.error('Error in direct language change:', error);
      return false;
    }
  }

  /**
   * Apply city change by directly calling the change function
   */
  applyCityChange() {
    try {
      
      // Check if we need to change city
      const storedCity = localStorage.getItem('city_value');
      const storedCityAlt = localStorage.getItem('city');
      const currentCity = storedCity || storedCityAlt;
      
      
      // If there's already a city selection and it's different from the saved one,
      // preserve the current selection instead of overriding it
      if (currentCity && currentCity !== this.selectedCity) {
        this.selectedCity = currentCity;
        return;
      }
      
      // Use global city matcher for comparison if available
      const isSameCity = window.CityMatcher ? window.CityMatcher.match(currentCity, this.selectedCity) : currentCity === this.selectedCity;
      if (isSameCity) {
        return;
      }

      // Try direct city update first (no visible interaction)
      if (this.tryDirectCityUpdate()) {
        return;
      }

      // Try to find and directly call the changeHeaderLocation function
      if (window.changeHeaderLocation) {
        this.callChangeHeaderLocationDirectly();
      } else {
        this.findAndClickCityOption();
      }
    } catch (error) {
      console.error('Error applying city change:', error);
    }
  }

  /**
   * Call changeHeaderLocation function directly without opening dropdown
   */
  callChangeHeaderLocationDirectly() {
    try {
      // Find the city options to get the display text
      const locationDropdown = document.getElementById('location-dropdown');
      if (!locationDropdown) {
        return;
      }

      const cityOptions = locationDropdown.querySelectorAll('.header-top-section__dropdown-item');
      let foundCity = false;
      
      cityOptions.forEach(option => {
        const cityText = option.textContent.trim();
        if (this.cityMatches(cityText, this.selectedCity)) {
          
          // Extract the onclick attribute to get the function call
          const onclickAttr = option.getAttribute('onclick');
          if (onclickAttr && onclickAttr.includes('changeHeaderLocation')) {
            
            // Parse the onclick to get city and displayText parameters
            const match = onclickAttr.match(/changeHeaderLocation\('([^']+)',\s*'([^']+)'\)/);
            if (match) {
              const cityCode = match[1];
              const displayText = match[2];
              
              // Call the function directly
              window.changeHeaderLocation(cityCode, displayText);
              foundCity = true;
            }
          }
        }
      });
      
      if (!foundCity) {
        this.findAndClickCityOption();
      }
    } catch (error) {
      console.error('Error calling changeHeaderLocation directly:', error);
      // Fallback to clicking method
      this.findAndClickCityOption();
    }
  }

  /**
   * Try to update city display directly without any visible interaction
   */
  tryDirectCityUpdate() {
    try {
      // Try to find the city display element and update it directly
      const locationDropdown = document.getElementById('location-dropdown');
      if (!locationDropdown) {
        return false;
      }

      const cityDisplay = locationDropdown.querySelector('.header-top-section__item span');
      if (!cityDisplay) {
        return false;
      }

      // Find the matching city option to get the display text
      const cityOptions = locationDropdown.querySelectorAll('.header-top-section__dropdown-item');
      let displayText = '';
      let cityCode = '';
      
      cityOptions.forEach(option => {
        const cityText = option.textContent.trim();
        if (this.cityMatches(cityText, this.selectedCity)) {
          // Get the display text from the onclick attribute
          const onclickAttr = option.getAttribute('onclick');
          if (onclickAttr && onclickAttr.includes('changeHeaderLocation')) {
            const match = onclickAttr.match(/changeHeaderLocation\('([^']+)',\s*'([^']+)'\)/);
            if (match) {
              cityCode = match[1];
              displayText = match[2];
            }
          }
        }
      });

      if (displayText && cityCode) {
        
        // Update the display text
        let newText = '';
        
        // Check current language and set appropriate prefix
        const currentLang = document.documentElement.lang || 'en';
        if (currentLang.startsWith('ar')) {
          newText = `يسلم إلى: ${displayText}`;
        } else {
          newText = `Deliver to: ${displayText}`;
        }
        
        cityDisplay.textContent = newText;
        
        // Check if city is actually changing before updating localStorage
        const storedCity = localStorage.getItem('city_value') || localStorage.getItem('city');
        const isCityActuallyChanging = window.CityMatcher ? 
          !window.CityMatcher.match(cityCode, storedCity) : 
          cityCode !== storedCity;
        
        // Update localStorage
        localStorage.setItem('city', cityCode);
        localStorage.setItem('city_value', cityCode);
        
        if (isCityActuallyChanging) {
          const cityChangeEvent = new CustomEvent('city-changed', {
            detail: {
              value: cityCode,
              textValue: cityCode,
              arabicTextValue: displayText,
            }
          });
          document.dispatchEvent(cityChangeEvent);
        } else {
        }
        
        return true;
      }
      
      return false;
    } catch (error) {
      console.error('Error in direct city update:', error);
      return false;
    }
  }

  /**
   * Alternative method: find and click city option (fallback)
   */
  findAndClickCityOption() {
    try {
      const locationDropdown = document.getElementById('location-dropdown');
      if (!locationDropdown) {
        return;
      }

      const cityOptions = locationDropdown.querySelectorAll('.header-top-section__dropdown-item');
      
      cityOptions.forEach(option => {
        const cityText = option.textContent.trim();
        // Check if this option matches our selected city
        if (this.cityMatches(cityText, this.selectedCity)) {
          option.click();
        }
      });
    } catch (error) {
      console.error('Error finding and clicking city option:', error);
    }
  }

  /**
   * Check if city text matches our selected city
   */
  cityMatches(displayText, selectedCity) {
    // Use global city matcher if available
    if (window.CityMatcher) {
      return window.CityMatcher.match(displayText, selectedCity);
    }
    
    // Fallback to local logic
    const normalizedDisplay = this.normalizeCityName(displayText);
    const normalizedSelected = this.normalizeCityName(selectedCity);
    
    // Direct match
    if (normalizedDisplay === normalizedSelected) {
      return true;
    }
    
    // Check for partial matches (e.g., "Riyadh" matches "Riyadh")
    if (normalizedDisplay.includes(normalizedSelected) || normalizedSelected.includes(normalizedDisplay)) {
      return true;
    }
    
    // Check for Arabic/English variations
    const cityMappings = {
      'riyadh': 'الرياض',
      'الرياض': 'riyadh',
      'jeddah': 'جدة',
      'جدة': 'jeddah',
      'al khobar': 'الخبر',
      'al-khobar': 'الخبر',
      'alkhobar': 'الخبر',
      'khobar': 'الخبر',
      'الخبر': 'al khobar',
      'dammam': 'الدمام',
      'الدمام': 'dammam',
      'dhahran': 'الظهران',
      'الظهران': 'dhahran',
      'jubail': 'الجبيل',
      'الجبيل': 'jubail',
      'qatif': 'الرياض',
      'القطيف' : 'qatif',
      'madinah':'المدينة',
      'المدينة':'madinah',
      'مكه':'meccah'

    };
    
    // Check direct mapping
    if (cityMappings[normalizedDisplay] === normalizedSelected || 
        cityMappings[normalizedSelected] === normalizedDisplay) {
      return true;
    }
    
    // Check if the display text contains any of the mapped values
    for (const [english, arabic] of Object.entries(cityMappings)) {
      if (normalizedDisplay.includes(english) || normalizedDisplay.includes(arabic)) {
        if (normalizedSelected.includes(english) || normalizedSelected.includes(arabic)) {
          return true;
        }
      }
    }
    
    // Additional flexible matching for edge cases
    const commonVariations = {
      'al khobar': ['al-khobar', 'al_khobar', 'alkhobar', 'khobar', 'الخبر'],
      'al khubar': ['al-khubar', 'al_khubar', 'alkhubar', 'khubar', 'الخبر'],
      'dammam': ['الدمام', 'damam'],
      'dhahran': ['الظهران', 'dharan'],
      'jubail': ['الجبيل', 'jubayl'],
      'madinah': ['المدينة', 'madinah'],
      'meccah': ['مكه', 'meccah'],

    };
    
    // Check common variations - normalize each variation for comparison
    for (const [base, variations] of Object.entries(commonVariations)) {
      const normalizedBase = base.replace(/[-\s_]+/g, ' ').trim();
      const normalizedVariations = variations.map(v => v.replace(/[-\s_]+/g, ' ').trim());
      
      const selectedMatches = normalizedSelected.includes(normalizedBase) || 
                             normalizedVariations.some(v => normalizedSelected.includes(v));
      const displayMatches = normalizedDisplay.includes(normalizedBase) || 
                            normalizedVariations.some(v => normalizedDisplay.includes(v));
      
      if (selectedMatches && displayMatches) {
        return true;
      }
    }
    
    return false;
  }

  /**
   * Normalize city name for better matching
   */
  normalizeCityName(cityName) {
    if (!cityName) return '';
    
    let normalized = cityName.toLowerCase().trim();
    
    // Remove common prefixes/suffixes
    normalized = normalized.replace(/^(city of|deliver to|يسلم إلى:?)\s*/i, '');
    normalized = normalized.replace(/\s+(city|مدينة)$/i, '');
    
    // Remove hyphens and normalize spaces for consistent matching
    normalized = normalized.replace(/[-\s]+/g, ' ').trim();
    
    // Handle common abbreviations
    normalized = normalized.replace(/\balk\b/g, 'al ');
    normalized = normalized.replace(/\bkh\b/g, 'khobar');
    
    return normalized;
  }
}

// Initialize popup when DOM is ready
let fnpPopup = null;

document.addEventListener('DOMContentLoaded', () => {
  fnpPopup = new LanguageCityPopup();
});

// Make popup instance globally available for debugging/testing
window.fnpPopup = fnpPopup;

// Make the clearSavedLanguageSelection method globally available
window.clearPopupSavedLanguage = () => {
  if (window.fnpPopup) {
    window.fnpPopup.clearSavedLanguageSelection();
  }
};

// Add CSS for screen reader only content
const srOnlyCSS = `
  .sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  }
`;

if (!document.getElementById('fnp-popup-sr-css')) {
  const style = document.createElement('style');
  style.id = 'fnp-popup-sr-css';
  style.textContent = srOnlyCSS;
  document.head.appendChild(style);
}
